import { convertStorageFormatToHtml } from '@databases/copy-paste/copy-helper';
import { renderDatabaseAsync, renderDatabaseEntry } from '@databases/copy-paste/render';
import { logError } from '@databases/logs';
import { type DefaultRootState, type DefaultStore } from '@databases/redux';
import { ExportFileType, RenderContext } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import {
  getCsvRepresentation,
  loadDependenciesAsync
} from '@databases/utils/database/field-types';
import {
  generateAllValues,
  getDatabaseEntries,
  getDatabaseEntryRequired,
  getDatabaseFields,
  getDatabaseValue,
  getEntryName,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import {
  applyDatabaseView,
  getFilteredValuesWithContext
} from '@databases/utils/database/view/view';
import { TranslatableError } from '@databases/utils/errors';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { deserializeError } from '@databases/utils/utils';
import { unparse } from 'papaparse';
import type { IntlShape } from 'react-intl-next';

export async function exportDatabase(
  {
    contentId,
    exportType,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
    databaseName
  }: {
    contentId: string;
    exportType: ExportFileType;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    databaseName: string;
  },
  store: DefaultStore,
  intl: IntlShape
): Promise<{ file: File; hadDependencyErrors: boolean }> {
  const database = selectDatabase(store.getState(), { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new TranslatableError(
          'Database is not loaded.',
          'snackbar-error-message.database-is-not-loaded'
        );
  }

  // make sure everything's loaded for export
  let firstDependencyError: Error | undefined;
  await Promise.all(
    (viewSettings && viewSettings.filter
      ? getFilteredValuesWithContext(database, viewSettings.filter)
      : Array.from(generateAllValues(database))
    ).map((context) => {
      return Promise.race([
        loadDependenciesAsync(
          {
            contentId: context.database.contentId,
            field: context.field,
            entryId: context.entry.id,
            value: context.value
          },
          store,
          intl
        ).catch((e: unknown) => {
          firstDependencyError ??= e instanceof Error ? e : new Error(String(e));
        }),
        new Promise((resolve) => setTimeout(() => resolve(true), 5000))
      ]);
    })
  );
  if (firstDependencyError) {
    logError('Failed to load export dependencies', firstDependencyError);
  }

  const hadDependencyErrors = firstDependencyError !== undefined;

  switch (exportType) {
    case ExportFileType.HTML:
      return {
        file: new File(
          [
            await exportDatabaseToHtml(
              {
                contentId,
                renderContext: RenderContext.EXPORT_DATABASE,
                viewSettings,
                selectDatabase,
                databaseName
              },
              store.getState(),
              intl
            )
          ],
          `${databaseName}.html`,
          { type: 'text/html' }
        ),
        hadDependencyErrors
      };

    case ExportFileType.CSV:
      return {
        file: new File(
          [
            '\uFEFF' +
              exportDatabaseToCSV(
                { contentId, viewSettings, selectDatabase },
                store.getState()
              )
          ],
          `${databaseName}.csv`,
          { type: 'text/csv;charset=utf-8' }
        ),
        hadDependencyErrors
      };

    default:
      throw new Error(`Export to ${exportType} not supported.`);
  }
}

function exportDatabaseToCSV(
  {
    contentId,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional
  }: {
    contentId: string;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): string {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const databaseView = applyDatabaseView(database, database, viewSettings, state);
  const fields = getDatabaseFields(databaseView);
  const fieldNames = fields.map((field) => field.name);
  const entries = getDatabaseEntries(databaseView).map((entry) => {
    return fields.map((field) => {
      const value = getDatabaseValue(database, field, entry);
      const stringRepresentation = getCsvRepresentation(
        { database: databaseView, field, entry, value },
        state
      );
      return stringRepresentation || '';
    });
  });

  return unparse<string[]>([fieldNames, ...entries], { skipEmptyLines: true });
}

/**
 * Used in HTML and PDF export. Returns HTML representation of the database with styling.
 */
export async function exportDatabaseToHtml(
  {
    contentId,
    renderContext,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
    databaseName
  }: {
    contentId: string;
    renderContext: RenderContext.EXPORT_DATABASE | RenderContext.PRINT_PREVIEW;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    databaseName: string;
  },
  state: DefaultRootState,
  intl: IntlShape
): Promise<string> {
  return await convertStorageFormatToHtml(
    // Since the media url is needed in the html, the async code path needs to be
    // called here.
    await renderDatabaseAsync(
      {
        contentId,
        renderContext,
        viewSettings,
        selectDatabase,
        showName: true,
        databaseName
      },
      state,
      intl
    ),
    databaseName,
    state,
    false
  );
}

export async function exportEntryToHtml(
  {
    contentId,
    entryId,
    renderContext,
    selectDatabase = selectDatabaseByContentIdOptional
  }: {
    contentId: string;
    entryId: string;
    renderContext:
      | RenderContext.EXPORT_DATABASE
      | RenderContext.PRINT_PREVIEW
      | RenderContext.COPY_DATABASE_TO_CLIPBOARD;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): Promise<string> {
  const database = selectDatabase(state, { contentId });
  if (!database || isBrokenDatabase(database)) {
    throw database?.error
      ? deserializeError(database.error)
      : new Error('Database is not loaded.');
  }
  const entry = getDatabaseEntryRequired(database, entryId);
  return await convertStorageFormatToHtml(
    await renderDatabaseEntry(
      { contentId, entryId, renderContext, selectDatabase, showName: true },
      state
    ),
    getEntryName({ database, entry }, state) || '',
    state,
    renderContext === RenderContext.COPY_DATABASE_TO_CLIPBOARD
  );
}
